import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from '@patternfly/react-core';
import { Td, Tr } from '@patternfly/react-table';

import { StoreState } from '../../../store/store';
import { dicomTagAttributeSet, dicomTagRemoved, dicomTagValueSet, imageSelectors } from '../../../store/image/imageSlice';
import { columnCssNames, columnIds } from './DicomTagTable';
import { ItemActionsColumn } from '../ItemActionsColumn';
import TextInputWithPresets from '../../../components/text-input-with-presets';
import RegexField from '../../../components/regex-field';
import { CustomizationObjectType } from '../../../store/global-types/customization-types';

interface DicomTagTableItemProps {
    dicomTagId: string;
    dicomTagIdList: string[];
    presets: string[];
}

const DicomTagTableItem = (props: DicomTagTableItemProps) => {

    const { dicomTagId, dicomTagIdList, presets } = props;

    const dispatch = useDispatch();
    const { t } = useTranslation();

    const dicomTag = useSelector((state: StoreState) => imageSelectors.selectDicomTagById(state, dicomTagId));
    const validationError = useSelector((state: StoreState) => imageSelectors.selectCustomizationValidationError(state, dicomTagId));

    const handleDicomAttributeChanged = useCallback((attribute: string) => {
        if (!dicomTagId) { throw new Error('DICOM tag is not defined'); }

        dispatch(dicomTagAttributeSet({ id: dicomTagId, attribute: attribute }));
    }, [dispatch, dicomTagId]);

    const handleDicomValueChanged = useCallback((value: string) => {
        if (!dicomTagId) { throw new Error('DICOM tag is not defined'); }

        dispatch(dicomTagValueSet({ id: dicomTagId, value: value }));
    }, [dispatch, dicomTagId]);

    const handleRemoveDicomTag = useCallback(() => {
        dispatch(dicomTagRemoved(dicomTagId));
    }, [dispatch, dicomTagId]);

    if (dicomTag === undefined) {
        return null;
    }

    const hasValidationError = validationError !== undefined && validationError.type === CustomizationObjectType.DicomTag;

    return (
        <>
            <Tr className={`roi-item ${hasValidationError ? 'validation-error' : undefined}`}>
                <Td className={columnCssNames.modified}><div className="roi-item-is-modified" title="This customization has unsaved changes.">{dicomTag.isModified ? '*' : <>&nbsp;</>}</div></Td>

                <Td dataLabel={columnIds.dicomAttribute} className={columnCssNames.dicomAttribute} data-testid="image-dicom-tag-attribute-cell">
                    <div>
                        <TextInputWithPresets
                            fieldId={`dicom-tag-${dicomTagId}-attribute`}
                            presetsTitle={t('customizationPage.image.dicomRestrictionSection.dicomTags.presets.title')}
                            tooltip={t('customizationPage.image.dicomRestrictionSection.dicomTags.presets.tooltip')}
                            onTextChange={handleDicomAttributeChanged}
                            defaultValue={dicomTag.attribute}
                            placeholder={t('customizationPage.image.dicomRestrictionSection.dicomTags.attribute.placeholder')}
                            showFreeTextEntryPreset
                            presets={presets}
                        />
                    </div>
                </Td>

                <Td dataLabel={columnIds.dicomValue} className={columnCssNames.dicomValue} data-testid="image-dicom-value-attribute-cell">
                    <div>
                        <RegexField
                            id={`dicom-tag-${dicomTagId}-value`}
                            label={undefined}
                            onChange={handleDicomValueChanged}
                            regexValue={dicomTag.value}
                            placeholder={t('customizationPage.image.dicomRestrictionSection.dicomTags.value.placeholder')}
                            enableComposer
                        />
                    </div>
                </Td>

                <Td isActionCell>
                    <ItemActionsColumn items={[
                        {
                            title: t('customizationPage.image.dicomRestrictionSection.dicomTags.removeDicomTag'),
                            onClick: handleRemoveDicomTag,
                        },
                    ]} />
                </Td>
            </Tr>

            {hasValidationError && (
                <Tr className="roi-item validation-error validation-error-box selectable">
                    <Td dataLabel="Validation error" noPadding={true} colSpan={100}>
                        <Alert variant="danger" isInline isPlain isExpandable title={t('common.validationError.message', { error: validationError.message })}>
                            {validationError.field && (<div>Field: {validationError.field}</div>)}
                            <div>Error type: {validationError.detail}</div>
                            <div>Error ctx: {validationError.ctx}</div>
                        </Alert>
                    </Td>
                </Tr>
            )}

        </>
    );
}

export default DicomTagTableItem;
